import { IconCheck, IconSelector } from "@tabler/icons-react";
import type { ReactNode } from "react";
import {
  Button,
  ListBox,
  ListBoxItem,
  Select,
  Text,
  type Key,
} from "react-aria-components";
import { Label } from "../Label/label.js";
import { menuStyle } from "../Menu/menuStyle.js";
import { SimpleMenuPopover } from "../Menu/simpleMenuPopover.js";
import { Size } from "../helpers/helpers.js";
import { selectStyle } from "./style.js";

/** String value used to represent the null/empty option. */
const nullKey = "_null";
const valueToKey = (value: Key | null) => (value === null ? nullKey : value);
const keyToValue = (key: Key) => (key === nullKey ? null : key);

export interface SimpleSelectProps<T extends Key | null> {
  leftAccessory?: ReactNode;
  label?: string;
  isLabelHidden?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /** Stretch the trigger button to fill its container instead of sizing to content. */
  isFullWidth?: boolean;
  /** Optional className applied to the options popover. Use to constrain the menu width. */
  popoverClassName?: string;
  placeholder?: ReactNode;
  options: readonly SimpleSelectOption<T>[];
  disabledValues?: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
}

export interface SimpleSelectOption<T extends Key | null> {
  value: T;
  icon?: ReactNode;
  label: string;
  description?: string;
}

export const SimpleSelect = <T extends Key | null>(
  props: SimpleSelectProps<T>,
) => {
  const selectedOption = props.options.find(
    (option) => option.value === props.value,
  );

  const style = selectStyle({
    isInvalid: props.isInvalid,
    size: props.size,
  });

  return (
    <Select
      className={!props.isLabelHidden && props.label ? "min-w-0" : "contents"}
      isDisabled={props.isDisabled}
      isInvalid={props.isInvalid}
      disabledKeys={props.disabledValues?.map(valueToKey)}
      value={valueToKey(props.value)}
      onChange={(key) => {
        const value = key === null ? null : keyToValue(key);
        const option = props.options.find((option) => option.value === value);
        if (!option) {
          throw new Error("Selected key not found in options");
        }

        props.onChange(option.value);
      }}
      aria-label={props.isLabelHidden ? props.label : undefined}
    >
      {!props.isLabelHidden && props.label ? (
        <Label size={Size.SMALL} className="mb-2">
          {props.label}
        </Label>
      ) : null}
      <Button
        className={style.button({
          className: props.isFullWidth ? "w-full" : undefined,
        })}
      >
        {props.leftAccessory ? (
          <div className="shrink-0 text-gray-700">{props.leftAccessory}</div>
        ) : null}
        {selectedOption &&
        (selectedOption.value !== null || !props.placeholder) ? (
          <>
            {selectedOption.icon ? (
              <div className="shrink-0 text-gray-700">
                {selectedOption.icon}
              </div>
            ) : null}
            <div className={style.value()}>{selectedOption.label}</div>
          </>
        ) : (
          <div className={style.placeholder()}>{props.placeholder}</div>
        )}
        <IconSelector className={style.chevron()} />
      </Button>
      <SimpleMenuPopover className={props.popoverClassName}>
        <ListBox
          items={props.options}
          disallowEmptySelection={props.options.every(
            (option) => option.value !== null,
          )}
        >
          {(option) => (
            <ListBoxItem
              id={valueToKey(option.value)}
              textValue={option.label}
              className={menuStyle().item()}
            >
              {option.icon ? (
                <div className="shrink-0">{option.icon}</div>
              ) : null}
              <div className="min-w-0 flex-1">
                <Text
                  slot="label"
                  className="block truncate text-sm leading-5 font-medium text-foreground group-selected:font-semibold"
                >
                  {option.label}
                </Text>
                {option.description ? (
                  <Text
                    slot="description"
                    className="mt-0.5 block text-xs leading-4 font-normal text-muted-foreground"
                  >
                    {option.description}
                  </Text>
                ) : null}
              </div>
              <IconCheck
                className={menuStyle().itemIcon({
                  className: "invisible text-indigo-600 group-selected:visible",
                })}
              />
            </ListBoxItem>
          )}
        </ListBox>
      </SimpleMenuPopover>
    </Select>
  );
};

export function getSimpleSelectOptionLabel<T extends Key | null>(
  options: readonly SimpleSelectOption<T>[],
  selectedId: T | undefined,
): string | undefined {
  if (selectedId === undefined) {
    return undefined;
  }

  const option = options.find((option) => option.value === selectedId);

  return option?.label;
}
